/* eslint-disable no-plusplus */
import React, {
  Children,
  cloneElement,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames, { Argument } from "classnames/dedupe";
import useEventListener from "../../../hooks/useEventListener";
import useIsMounted from "../../../hooks/useIsMounted";
import useWatchState from "../../../hooks/useWatchState";
import { MinusIconSmall, PlusIconSmall } from "../../../icons";
import { Button } from "../../ActionsSelectors/Button";
import {
  getDimensions,
  getInnerDimensions,
  getOuterDimensions,
  IItemDimensions,
  maxItemsFit,
} from "./elementDimensions";
import HiddenPortal from "./HiddenPortal";
import { useIsomorphicLayoutEffect } from "../../../hooks";
import useMountedState from "../../../hooks/useMountedState";
import { getDefaultScrollConfig } from "../../../helpers";

export interface IResponsiveContainerProps {
  /**
   * Set if container is expanded.
   * Default `false`
   */
  isExpanded?: boolean;
  /**
   * The custom set of css classes used to style component
   */
  classes?: {
    /**
     * applied to outer 'div' container
     */
    root?: Argument;
  };

  /**
   * Set translation text for labels
   */

  labels?: {
    more: ReactNode;
    less: ReactNode;
  };
  children?: ReactNode;
}

const listenerConfig = getDefaultScrollConfig();

/**
 *  Render flexible container with expand button.
 *
 */
const ResponsiveContainer: FC<IResponsiveContainerProps> = ({
  isExpanded = false,
  labels = {
    more: "More",
    less: "Less",
  },
  classes,
  children,
}) => {
  const childrenLength = Children.toArray(children).length;

  /**
   * Initial state
   */

  // For SSR just render all children as is
  const isMounted = useIsMounted();
  const [displayItemsNum, setDisplayItemsNum] = useState<number>(
    isMounted() ? 0 : childrenLength,
  );

  const [childrenDimensions, setChildrenDimensions] = useState<
    IItemDimensions[]
  >([]);

  const [isExpandedState, setIsExpanded] = useWatchState<boolean>({
    value: isExpanded,
  });

  const [isButtonShown, setIsButtonShown] = useState<boolean>(false);

  /** Refs */
  const displayContainer = useRef<HTMLDivElement>(null);
  const renderContainer = useRef<HTMLDivElement>(null);
  const expandBtn = useRef<HTMLButtonElement>(null);

  /**
   * Calc max item fit into container and update state
   */
  const updateDisplayItemsCount = useCallback(() => {
    // First fit calculation
    let maxItems = maxItemsFit(
      childrenDimensions,
      getInnerDimensions(displayContainer.current).width,
    );

    /**
     * If not all children fits into container, run recalculation with
     * expand button present in container
     */
    if (maxItems < childrenLength) {
      maxItems = maxItemsFit(
        childrenDimensions,
        getInnerDimensions(displayContainer.current).width -
          getOuterDimensions(expandBtn.current).width,
      );
    }

    // Set btn visibility and children size
    setDisplayItemsNum(isExpandedState ? childrenLength : maxItems);
    setIsButtonShown(maxItems < childrenLength);
  }, [childrenDimensions, childrenLength, isExpandedState]);

  const isMountedState = useMountedState();
  /**
   * Run children dimensions recalculation on component mount
   * and children prop change
   */
  useIsomorphicLayoutEffect(() => {
    if (isMountedState && renderContainer?.current) {
      setChildrenDimensions(getDimensions(renderContainer, childrenLength));
    }
  }, [children, childrenLength, isMountedState]);

  /**
   * Run layout update and render after children dimension recalculation /
   * children amount change / expand-collapse state change
   */
  useEffect(() => {
    if (displayContainer?.current && expandBtn?.current) {
      updateDisplayItemsCount();
    }
  }, [updateDisplayItemsCount]);

  /**
   * Run layout update and render on window or container resize
   */
  useEventListener("resize", updateDisplayItemsCount, listenerConfig);
  useEventListener("scroll", updateDisplayItemsCount, listenerConfig);

  /**
   * Expand button callback fn
   * Toggles all children to be displayed / or back to single line view
   */
  const handleExpand = useCallback(() => {
    setIsExpanded(!isExpandedState);
  }, [isExpandedState, setIsExpanded]);

  // Expand button element with state and handler
  const expandButton = (
    <Button
      data-testid="ResponsiveContainer__expand"
      classes={{ root: "btn-tag--more me-4 mb-4" }}
      variant="tag"
      onClick={handleExpand}
    >
      {isExpandedState ? labels?.less : labels?.more}
      {isExpandedState ? <MinusIconSmall /> : <PlusIconSmall />}
    </Button>
  );

  // Browser paint hidden element with `ref`, render to get actual dimensions
  const buttonRenderReference = cloneElement(expandButton, {
    ref: expandBtn,
  });

  const slicedChildren = Children.toArray(children).slice(0, displayItemsNum);

  return (
    <>
      {/* Container for pre-rendering of children dimensions */}
      <HiddenPortal ref={renderContainer}>{children}</HiddenPortal>

      {/* Container for pre-rendering of expand button dimensions */}
      <HiddenPortal>{buttonRenderReference}</HiddenPortal>

      <div ref={displayContainer} className={classNames(classes?.root)}>
        {slicedChildren}
        {isButtonShown && expandButton}
      </div>
    </>
  );
};

export default ResponsiveContainer;
